import { z } from "zod";
import { ActivityInputSchema, CookingEnjoymentSchema, CookingSkillSchema, GoalTypeSchema, HealthConditionSchema, PlanningModeSchema, SnackPreferenceSchema, LactoseLevelSchema, } from "./profile.js";
import { LocaleSchema, explicitListOrNone } from "./primitives.js";
/**
 * 12-step mandatory onboarding (ui-ux-spec.md "Mandatory onboarding" table).
 * Every step requires an explicit answer or an explicit "none"/not-applicable
 * choice — never a silent skip or a fabricated default.
 */
export const DietaryStyleSchema = z.enum([
    "omnivore",
    "pescatarian",
    "vegetarian",
    "vegan",
]);
export const PreferNotToAnswerFieldSchema = z.enum([
    "sex",
    "age",
    "height",
    "weight",
    "targetWeight",
]);
export const Step1Schema = z.object({
    step: z.literal(1),
    answer: z.object({ language: LocaleSchema }),
});
export const Step2Schema = z.object({
    step: z.literal(2),
    answer: z.object({
        ageEligible18Plus: z.literal(true),
        country: z.literal("DE"),
        // `postcode` removed 2026-08-27 (ADR-028 amendment): users are never
        // asked for a zip code and nothing may rely on it. Legacy stored step-2
        // answers still carry a `postcode` key; this plain z.object() strips
        // unknown keys on re-parse, so those answers keep parsing (key dropped).
        /**
         * Personal record (2026-08 profile batch, migration 0021): name + date of
         * birth are captured here — the step that already carries the 18+
         * confirmation, so the DOB is asked exactly where its consequence lives.
         * Optional at the SCHEMA level only for backward compatibility (same
         * pattern as Step10Schema.snackPreference): step-2 answers stored before
         * this change have none of these keys and GET /v1/onboarding re-parses
         * stored answers through this schema. The onboarding UI enforces explicit
         * values for new users, and the server rejects a DOB implying <18 with
         * DOB_UNDERAGE (the eligibility claim and the DOB must agree — never a
         * silent override in either direction). Format-only here: age-bound
         * refines would have to re-evaluate against "now" when old answers are
         * reconstructed, so the authoritative 18-120 check lives in the
         * onboarding Lambda.
         */
        firstName: z.string().trim().min(1).max(100).optional(),
        familyName: z.string().trim().min(1).max(100).optional(),
        /** YYYY-MM-DD */
        dateOfBirth: z.iso.date().optional(),
    }),
});
/**
 * What a NEW step-2 submission must carry. `Step2Schema` stays lenient (the stored answers of
 * accounts onboarded before the profile batch have no date of birth and GET /v1/onboarding
 * re-parses them), but the 18+ confirmation is only credible next to an actual birthday: a PUT
 * without `dateOfBirth` is refused (the Lambda answers 400 DOB_REQUIRED before this schema is
 * consulted, so the code stays stable), and onboarding cannot complete without one on record.
 */
export const Step2PutSchema = z.object({
    step: z.literal(2),
    answer: Step2Schema.shape.answer.extend({
        /** YYYY-MM-DD */
        dateOfBirth: z.iso.date(),
    }),
});
export const Step3Schema = z.object({
    step: z.literal(3),
    answer: z.object({
        goalType: GoalTypeSchema,
        targetAcknowledged: z.boolean(),
    }),
});
export const Step4Schema = z.object({
    step: z.literal(4),
    answer: z
        .object({
        sex: z.enum(["female", "male"]).nullable(),
        // Bounds (ONB-A4.7, live-found: age 5 / height 3000cm computed a
        // "target"): 18+ product (step 2), physiological plausibility ranges —
        // out-of-range is a typo, never a target input.
        age: z.number().int().min(18).max(100).nullable(),
        heightCm: z.number().min(120).max(230).nullable(),
        weightKg: z.number().min(30).max(300).nullable(),
        targetWeightKg: z.number().min(30).max(300).nullable(),
        preferNotToAnswer: z.array(PreferNotToAnswerFieldSchema),
        // ADR-027 rule 7. OPTIONAL at the schema level only for backward
        // compatibility: step-4 answers stored before this change have no
        // `activity` key at all, and GET /v1/onboarding re-parses stored
        // answers through this schema (same pattern as Step2Schema's
        // firstName/dateOfBirth, Step10Schema's snackPreference). The
        // onboarding UI requires an explicit `level` for anyone passing
        // through the step now (frontend/src/components/onboarding/steps.ts
        // STEP_SCHEMAS[4] wraps this with that gate); `level: null` inside a
        // present `activity` object is never valid for a NEW submission, only
        // reachable by re-parsing a legacy stored row that predates this field
        // entirely (which won't even have the key, so `.optional()` handles
        // it — `level` itself is nullable only so the calculator has one
        // honest "not answered" representation to fall back from either way).
        activity: ActivityInputSchema.optional(),
    })
        // Found live 2026-08-23: a null field WITHOUT its explicit
        // prefer-not-to-answer token slipped through (sex unset), targets never
        // computed, and every plan job failed PROFILE_TARGETS_UNAVAILABLE with
        // a dead-end UX. ui-ux-spec.md: "every step enforces explicit answer/
        // none" — null is only valid as a DECLARED refusal. (targetWeightKg is
        // genuinely optional: maintain-goal users have none.)
        .superRefine((answer, ctx) => {
        const requireDeclared = [
            ["sex", "sex"],
            ["age", "age"],
            ["heightCm", "height"],
            ["weightKg", "weight"],
        ];
        for (const [field, token] of requireDeclared) {
            if (answer[field] === null && !answer.preferNotToAnswer.includes(token)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [field],
                    message: `${String(field)} may be null only when '${token}' is explicitly in preferNotToAnswer`,
                });
            }
        }
    }),
});
export const Step5Schema = z.object({
    step: z.literal(5),
    answer: z.object({ dietaryStyle: DietaryStyleSchema }),
});
export const Step6Schema = z.object({
    step: z.literal(6),
    answer: z.object({
        allergens: explicitListOrNone(z.string()),
        /** Free-text intolerances (histamine, FODMAPs …). Text that names a STRUCTURED subject (lactose) is rejected by the server: use `structuredIntolerances`. */
        intolerances: explicitListOrNone(z.string()),
        /** ADR-080: structured intolerances with the user-chosen level (lactose today). Optional so legacy stored answers still parse. */
        structuredIntolerances: z.array(z.object({ subjectId: z.literal("lactose"), level: LactoseLevelSchema })).optional(),
    }),
});
export const Step7Schema = z.object({
    step: z.literal(7),
    answer: z.object({
        neverEat: explicitListOrNone(z.string()),
        dislikedCombinations: explicitListOrNone(z.string()),
        dislikes: explicitListOrNone(z.string()),
        likes: explicitListOrNone(z.string()),
    }),
});
export const Step8Schema = z.object({
    step: z.literal(8),
    answer: z.object({
        cookingEnjoyment: CookingEnjoymentSchema,
        cookingSkill: CookingSkillSchema,
    }),
});
export const Step9Schema = z.object({
    step: z.literal(9),
    answer: z.object({
        timePerMealMinutes: z.number().int().positive(),
        timePerDayMinutes: z.number().int().positive(),
        equipment: explicitListOrNone(z.string()),
    }),
});
export const Step10Schema = z.object({
    step: z.literal(10),
    answer: z.object({
        planningMode: PlanningModeSchema,
        householdServings: z.number().int().min(1),
        /**
         * ADR-023 snack timing sub-question of the meal-structure step. Optional
         * at the SCHEMA level only for backward compatibility: step-10 answers
         * stored before ADR-023 have no snackPreference key, and GET
         * /v1/onboarding re-parses stored answers through this schema — a
         * required field would break every existing user's onboarding state. The
         * onboarding UI still enforces an explicit choice (all four options
         * shown, none preselected, Next disabled until one is picked — the
         * "explicit answer, never a silent default" rule). Server side, an
         * absent value simply leaves profiles.snack_preference at its 'none'
         * default (existing users unaffected).
         */
        snackPreference: SnackPreferenceSchema.optional(),
    }),
});
export const Step11Schema = z.object({
    step: z.literal(11),
    answer: z.object({
        /** Budget Model v2 (ADR-052, owner D-5): onboarding asks for ONE
         * optional amount — the SOFT weekly spending goal — and nothing else
         * (no tiers, no maximum checkout; the full controls live in Settings).
         * null = skipped / no goal. New signups are stored as budget model v2. */
        weeklyGoalEurCents: z.number().int().nonnegative().nullable(),
        /**
         * ADR-057 legacy compatibility (2026-09-05): answers stored before
         * Budget Model v2 carried `weeklyBudgetEurCents` (a HARD weekly limit —
         * ADR-052's `legacy_hard_cap`) or `hasNoBudgetTarget`. They are served
         * as recorded — the amount under this field, never reinterpreted as a
         * goal — and `weeklyGoalEurCents` is null for them. The stored answer is
         * not rewritten. Absent on v2 answers.
         */
        legacyWeeklyBudgetEurCents: z.number().int().nonnegative().nullable().optional(),
        preferredRetailers: explicitListOrNone(z.string()),
        deliveryPreference: z.enum(["pickup", "delivery", "either", "none"]),
    }),
});
export const PantryQuickAddItemSchema = z.object({
    sourceText: z.string().min(1),
});
export const Step12Schema = z.object({
    step: z.literal(12),
    answer: z.object({
        pantryStarter: z.union([
            z.literal("nothing_to_add_now"),
            z.array(PantryQuickAddItemSchema),
        ]),
        /** Personalization confirmation — consent itself was already recorded at signup (ADR-012). */
        reviewConfirmed: z.literal(true),
    }),
});
/**
 * Step 13 — mandatory health-condition declaration (ADR-013 Decision 3, T04a).
 * Appended after the original 12-step flow (rather than renumbering steps
 * 5-12) to avoid rippling through every existing step-number reference in
 * this package and its fixtures; see the T04a handoff notes. Conditions
 * never unlock therapeutic diet planning (pilot non-goal) — they only drive
 * deterministic food-safety exclusions (services/db conditionDerivations),
 * disclaimers, and unsafe-goal blocking, all implemented elsewhere.
 */
const Step13AnswerSchema = z
    .object({
    conditions: z.array(HealthConditionSchema),
    /** Required when `conditions` includes 'other'; null otherwise. */
    otherText: z.string().nullable(),
    /** Free-text current medications; explicitly allowed to be empty. */
    medications: z.array(z.string()),
    /** Explicit "no conditions" answer — never a silent skip. */
    none: z.boolean(),
})
    .superRefine((val, ctx) => {
    const hasConditions = val.conditions.length > 0;
    if (val.none === hasConditions) {
        ctx.addIssue({
            code: "custom",
            path: ["none"],
            message: "none must be true if and only if conditions is empty (explicit XOR)",
        });
    }
    if (val.conditions.includes("other") && !val.otherText) {
        ctx.addIssue({
            code: "custom",
            path: ["otherText"],
            message: "otherText is required when conditions includes 'other'",
        });
    }
});
export const Step13Schema = z.object({
    step: z.literal(13),
    answer: Step13AnswerSchema,
});
export const OnboardingStepAnswerSchema = z.discriminatedUnion("step", [
    Step1Schema,
    Step2Schema,
    Step3Schema,
    Step4Schema,
    Step5Schema,
    Step6Schema,
    Step7Schema,
    Step8Schema,
    Step9Schema,
    Step10Schema,
    Step11Schema,
    Step12Schema,
    Step13Schema,
]);
export const OnboardingStepNumberSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
    z.literal(10),
    z.literal(11),
    z.literal(12),
    z.literal(13),
]);
// PUT /v1/onboarding/{step} request body (path `step` must match `body.step`).
// Same as the stored-answer union except step 2 REQUIRES the date of birth (Step2PutSchema).
export const OnboardingStepPutRequestSchema = z.discriminatedUnion("step", [
    Step1Schema,
    Step2PutSchema,
    Step3Schema,
    Step4Schema,
    Step5Schema,
    Step6Schema,
    Step7Schema,
    Step8Schema,
    Step9Schema,
    Step10Schema,
    Step11Schema,
    Step12Schema,
    Step13Schema,
]);
// GET /v1/onboarding response
export const OnboardingStateResponseSchema = z.object({
    currentStep: OnboardingStepNumberSchema,
    completedSteps: z.array(OnboardingStepNumberSchema),
    status: z.enum(["not_started", "in_progress", "completed"]),
    answers: z.array(OnboardingStepAnswerSchema),
});
// Response to PUT /v1/onboarding/{step} — echoes updated state; on step 12
// completion also carries the triggered plan job id (T07 dependency, stubbed
// as nullable so this contract does not block on plan generation landing).
export const OnboardingStepPutResponseSchema = OnboardingStateResponseSchema.extend({
    triggeredPlanJobId: z.string().nullable(),
});
//# sourceMappingURL=onboarding.js.map